import { Event } from "../lib/types";
import { ProfessionPreference } from "../lib/professionTypes";
import { User } from "../contexts/auth/types";

// Constants for feature weighting
const WEIGHTS = {
  professionMatch: 0.5,
  timeRelevance: 0.3,
  locationProximity: 0.15,
  popularityFactor: 0.05
};

/**
 * Calculates a similarity score based on cosine similarity of profession vectors
 * This is a simplified version of cosine similarity calculation for demonstration
 */
export function calculateProfessionSimilarity(
  eventProfessions: string[],
  userPreferences: ProfessionPreference[]
): number {
  if (!eventProfessions?.length || !userPreferences?.length) return 0;

  // Create feature vectors (simple presence/absence with weights)
  let vectorProduct = 0;
  let eventMagnitude = 0;
  let userMagnitude = 0;

  // Get preference levels mapped to numerical values
  const preferenceMap = userPreferences.reduce((map, pref) => {
    if (pref && pref.professionId) {
      map[pref.professionId] = pref.level === 'high' ? 3 : pref.level === 'medium' ? 2 : 1;
    }
    return map;
  }, {} as Record<string, number>);

  // Calculate vector product and magnitudes
  eventProfessions.forEach(prof => {
    if (prof) {
      const eventValue = 1; // Binary presence
      const userValue = preferenceMap[prof] || 0;

      vectorProduct += eventValue * userValue;
      eventMagnitude += eventValue * eventValue;
    }
  });

  userPreferences.forEach(pref => {
    if (pref && pref.level) {
      const value = pref.level === 'high' ? 3 : pref.level === 'medium' ? 2 : 1;
      userMagnitude += value * value;
    }
  });

  // Avoid division by zero
  if (eventMagnitude === 0 || userMagnitude === 0) return 0;

  // Cosine similarity
  return vectorProduct / (Math.sqrt(eventMagnitude) * Math.sqrt(userMagnitude));
}

/**
 * Calculates a temporal relevance score based on the event date relative to current date
 * Events happening soon (but not too soon) get higher scores
 */
export function calculateTimeRelevance(eventDate: string): number {
  if (!eventDate) return 0;

  const now = new Date();
  const eventTime = new Date(eventDate);
  const diffDays = (eventTime.getTime() - now.getTime()) / (1000 * 3600 * 24);

  // Optimal range: events 7-30 days in the future get highest scores
  if (diffDays < 0) return 0; // Past events
  if (diffDays < 7) return 0.5 + (diffDays / 14); // Approaching events
  if (diffDays < 30) return 1.0; // Optimal range
  if (diffDays < 90) return 1 - ((diffDays - 30) / 60); // Further future events
  return 0.2; // Far future events
}

/**
 * Main recommendation function that combines multiple factors for personalized event recommendations
 */
export function getMLRecommendedEvents(
  events: Event[],
  user: User | null,
  limit = 10
): Event[] {
  if (!user || !user.userProfile.notificationPreferences?.preferredTopics?.length) {
    console.log("Cannot generate ML recommendations: missing user or preferences");
    return [];
  }

  console.log(`Generating ML recommendations based on ${user.userProfile.notificationPreferences.preferredTopics.length} preferred topics`);

  // Transform user topics to preference objects
  const userPreferences = user.userProfile.notificationPreferences.preferredTopics.map((topic: any) => ({
    professionId: topic,
    level: 'high' as const
  }));

  // Calculate scores for each event
  const scoredEvents = events
    .filter(event => event && event.dateStart) // Ensure valid events only
    .map(event => {
      // Profession similarity score (using ML-inspired cosine similarity)
      const professionScore = calculateProfessionSimilarity(
        event.professions || [],
        userPreferences
      );

      // Time relevance score
      const timeScore = calculateTimeRelevance(event.dateStart);

      // Popularity score (simplified version based on view count)
      const popularityScore = event.viewCount ? Math.min(event.viewCount / 100, 1) : 0;

      // Combined weighted score
      const totalScore =
        (professionScore * WEIGHTS.professionMatch) +
        (timeScore * WEIGHTS.timeRelevance) +
        (popularityScore * WEIGHTS.popularityFactor);

      return {
        ...event,
        mlMatchScore: totalScore,
        professionSimilarity: professionScore,
        timeRelevance: timeScore,
        isRecommended: true
      };
    });

  console.log(`Scored ${scoredEvents.length} events for ML recommendations`);

  // Sort by score and return top recommendations
  return scoredEvents
    .sort((a, b) => (b.mlMatchScore || 0) - (a.mlMatchScore || 0))
    .slice(0, limit);
}

/**
 * Explains the ML recommendation in human-readable terms
 */
export function explainRecommendation(event: Event): string {
  if (!event.mlMatchScore) return "Ez az esemény az általános érdeklődésed alapján lett ajánlva.";

  const factors = [];

  if (event.professionSimilarity && event.professionSimilarity > 0.5) {
    factors.push("szakmai érdeklődésed");
  }

  if (event.timeRelevance && event.timeRelevance > 0.7) {
    factors.push("az optimális időzítés");
  }

  if (event.viewCount && event.viewCount > 50) {
    factors.push("az esemény népszerűsége");
  }

  if (factors.length === 0) {
    return "Ez az esemény az általános preferenciáid alapján került ajánlásra.";
  }

  return `Ez az esemény főként a következők miatt lett ajánlva: ${factors.join(', ')}.`;
}
